// frontend/src/pages/superAdmin/ReportOverview.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/Header';
import Sidebar from '../../components/Sidebar';
import Footer from '../../components/Footer';
import api from '../../services/api';
import Swal from 'sweetalert2';
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';

// Register ChartJS components
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const ReportOverview = ({ user, setUser }) => {
  const [loading, setLoading] = useState(true);
  const [fiscalYears, setFiscalYears] = useState([]);
  const [activeFiscalYear, setActiveFiscalYear] = useState(null);
  const [selectedFiscalYear, setSelectedFiscalYear] = useState(null);
  const [schoolScores, setSchoolScores] = useState([]);
  const [chartData, setChartData] = useState(null);

  // Fetch initial data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Get active fiscal year
        const fiscalYearRes = await api.get('/fiscal-years/active');
        const activeYear = fiscalYearRes.data;
        setActiveFiscalYear(activeYear);
        
        // Get all fiscal years
        const fiscalYearsRes = await api.get('/fiscal-years');
        setFiscalYears(fiscalYearsRes.data);
        
        if (activeYear) {
          setSelectedFiscalYear(activeYear.id);
          await loadReportData(activeYear.id);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        Swal.fire({
          icon: 'error',
          title: 'เกิดข้อผิดพลาด',
          text: 'ไม่สามารถโหลดข้อมูลได้ กรุณาลองใหม่อีกครั้ง',
          confirmButtonText: 'ตกลง'
        });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Load report data when fiscal year changes
  const loadReportData = async (fiscalYearId) => {
    try {
      setLoading(true);
      
      // Get all schools with scores
      const scoresRes = await api.get(`/reports/all/${fiscalYearId}`);
      const scores = scoresRes.data;
      setSchoolScores(scores);
      
      // Prepare chart data
      prepareChartData(scores);
    } catch (error) {
      console.error('Error loading report data:', error);
      Swal.fire({
        icon: 'error',
        title: 'เกิดข้อผิดพลาด',
        text: 'ไม่สามารถโหลดข้อมูลรายงานได้ กรุณาลองใหม่อีกครั้ง',
        confirmButtonText: 'ตกลง'
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle fiscal year change
  const handleFiscalYearChange = async (e) => {
    const yearId = e.target.value;
    setSelectedFiscalYear(yearId);
    await loadReportData(yearId);
  };

  // Prepare chart data
  const prepareChartData = (scores) => {
    if (!scores || scores.length === 0) {
      setChartData(null);
      return;
    }

    // Get top 10 schools by total score
    const top10Schools = [...scores].sort((a, b) => b.total_score - a.total_score).slice(0, 10);

    const data = {
      labels: top10Schools.map(school => school.school_name),
      datasets: [
        {
          label: 'IIT',
          data: top10Schools.map(school => parseFloat(school.iit_score.toFixed(2))),
          backgroundColor: 'rgba(54, 162, 235, 0.6)',
          borderColor: 'rgba(54, 162, 235, 1)',
          borderWidth: 1
        },
        {
          label: 'EIT',
          data: top10Schools.map(school => parseFloat(school.eit_score.toFixed(2))),
          backgroundColor: 'rgba(75, 192, 192, 0.6)',
          borderColor: 'rgba(75, 192, 192, 1)',
          borderWidth: 1
        },
        {
          label: 'OIT',
          data: top10Schools.map(school => parseFloat(school.oit_score.toFixed(2))),
          backgroundColor: 'rgba(255, 159, 64, 0.6)',
          borderColor: 'rgba(255, 159, 64, 1)',
          borderWidth: 1
        }
      ]
    };

    const options = {
      responsive: true,
      plugins: {
        legend: {
          position: 'top',
        },
        title: {
          display: true,
          text: '10 อันดับโรงเรียนที่ได้คะแนนสูงสุด',
        },
      },
      scales: {
        x: {
          stacked: false,
        },
        y: {
          stacked: false,
          beginAtZero: true,
          max: 100,
        },
      },
    };

    setChartData({ data, options });
  };

  // Export to Excel
  const exportToExcel = () => {
    if (!schoolScores || schoolScores.length === 0) {
      Swal.fire({
        icon: 'warning',
        title: 'ไม่มีข้อมูล',
        text: 'ไม่มีข้อมูลที่จะส่งออก',
        confirmButtonText: 'ตกลง'
      });
      return;
    }

    // Find the active fiscal year name
    const fiscalYear = fiscalYears.find(year => year.id === parseInt(selectedFiscalYear))?.year || '';

    // Create CSV content
    const headers = [
      'ลำดับ',
      'รหัสโรงเรียน',
      'ชื่อโรงเรียน',
      'คะแนน IIT',
      'คะแนน EIT1',
      'คะแนน EIT2',
      'คะแนน EIT รวม',
      'คะแนน OIT',
      'คะแนนรวมทั้งหมด',
      'ระดับผลการประเมิน'
    ];

    const csvContent = schoolScores.map((school, index) => [
      index + 1,
      school.school_code,
      school.school_name,
      parseFloat(school.iit_score.toFixed(2)),
      parseFloat(school.eit1_score.toFixed(2)),
      parseFloat(school.eit2_score.toFixed(2)),
      parseFloat(school.eit_score.toFixed(2)),
      parseFloat(school.oit_score.toFixed(2)),
      parseFloat(school.total_score.toFixed(2)),
      school.rating
    ]);

    // Add headers to the CSV content
    csvContent.unshift(headers);

    // Convert to CSV
    const csv = csvContent.map(row => row.join(',')).join('\n');

    // Create download link
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `ผลการประเมิน_ITA_${fiscalYear}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Count schools by rating
  const getRatingCounts = () => {
    if (!schoolScores || schoolScores.length === 0) return {};

    const counts = {
      'ผ่านดีเยี่ยม': 0,
      'ผ่านดี': 0,
      'ผ่าน': 0,
      'ต้องปรับปรุง': 0,
      'ต้องปรับปรุงโดยด่วน': 0
    };

    schoolScores.forEach(school => {
      if (counts[school.rating] !== undefined) {
        counts[school.rating]++;
      }
    });

    return counts;
  };

  const ratingCounts = getRatingCounts();

  return (
    <div className="wrapper">
      <Header user={user} title="รายงานผลการประเมิน" />
      <Sidebar user={user} />

      <div className="content-wrapper">
        <section className="content-header">
          <div className="container-fluid">
            <div className="row mb-2">
              <div className="col-sm-6">
                <h1>รายงานผลการประเมิน ITA</h1>
              </div>
              <div className="col-sm-6">
                <ol className="breadcrumb float-sm-right">
                  <li className="breadcrumb-item">
                    <Link to="/super-admin/dashboard">หน้าหลัก</Link>
                  </li>
                  <li className="breadcrumb-item active">รายงานผลการประเมิน</li>
                </ol>
              </div>
            </div>
          </div>
        </section>

        <section className="content">
          <div className="container-fluid">
            <div className="row mb-3">
              <div className="col-md-6">
                <div className="input-group">
                  <label className="input-group-text" htmlFor="fiscalYearSelect">ปีงบประมาณ:</label>
                  <select
                    id="fiscalYearSelect"
                    className="form-select"
                    value={selectedFiscalYear || ''}
                    onChange={handleFiscalYearChange}
                    disabled={loading}
                  >
                    <option value="">เลือกปีงบประมาณ</option>
                    {fiscalYears.map(year => (
                      <option key={year.id} value={year.id}>
                        ปีงบประมาณ {year.year} {year.active ? '(ปัจจุบัน)' : ''}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="col-md-6 text-end">
                <button
                  type="button"
                  className="btn btn-success"
                  onClick={exportToExcel}
                  disabled={!schoolScores.length}
                >
                  <i className="fas fa-file-excel me-2"></i> ส่งออกเป็น Excel
                </button>
              </div>
            </div>

            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">กำลังโหลด...</span>
                </div>
                <p className="mt-2">กำลังโหลดข้อมูล...</p>
              </div>
            ) : (
              <>
                <div className="row">
                  <div className="col-lg-4 col-12">
                    <div className="small-box bg-success">
                      <div className="inner">
                        <h3>{schoolScores.length || 0}</h3>
                        <p>จำนวนโรงเรียนทั้งหมด</p>
                      </div>
                      <div className="icon">
                        <i className="fas fa-school"></i>
                      </div>
                    </div>
                  </div>

                  <div className="col-lg-4 col-12">
                    <div className="small-box bg-info">
                      <div className="inner">
                        <h3>
                          {schoolScores.length > 0
                            ? parseFloat(
                                schoolScores.reduce((sum, school) => sum + school.total_score, 0) /
                                  schoolScores.length
                              ).toFixed(2)
                            : '0.00'}
                        </h3>
                        <p>คะแนนเฉลี่ยทั้งหมด</p>
                      </div>
                      <div className="icon">
                        <i className="fas fa-chart-line"></i>
                      </div>
                    </div>
                  </div>

                  <div className="col-lg-4 col-12">
                    <div className="small-box bg-warning">
                      <div className="inner">
                        <h3>
                          {schoolScores.length > 0
                            ? Math.max(...schoolScores.map(school => school.total_score)).toFixed(2)
                            : '0.00'}
                        </h3>
                        <p>คะแนนสูงสุด</p>
                      </div>
                      <div className="icon">
                        <i className="fas fa-trophy"></i>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-6">
                    <div className="card">
                      <div className="card-header">
                        <h3 className="card-title">สรุปผลการประเมิน</h3>
                      </div>
                      <div className="card-body">
                        <div className="table-responsive">
                          <table className="table table-bordered">
                            <thead>
                              <tr className="table-primary">
                                <th>ระดับผลการประเมิน</th>
                                <th>จำนวน</th>
                                <th>ร้อยละ</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr>
                                <td>ผ่านดีเยี่ยม (95.00-100.00)</td>
                                <td>{ratingCounts['ผ่านดีเยี่ยม'] || 0}</td>
                                <td>
                                  {schoolScores.length > 0
                                    ? (
                                        (ratingCounts['ผ่านดีเยี่ยม'] / schoolScores.length) *
                                        100
                                      ).toFixed(2)
                                    : '0.00'}
                                  %
                                </td>
                              </tr>
                              <tr>
                                <td>ผ่านดี (85.00-94.99)</td>
                                <td>{ratingCounts['ผ่านดี'] || 0}</td>
                                <td>
                                  {schoolScores.length > 0
                                    ? (
                                        (ratingCounts['ผ่านดี'] / schoolScores.length) *
                                        100
                                      ).toFixed(2)
                                    : '0.00'}
                                  %
                                </td>
                              </tr>
                              <tr>
                                <td>ผ่าน (75.00-84.99)</td>
                                <td>{ratingCounts['ผ่าน'] || 0}</td>
                                <td>
                                  {schoolScores.length > 0
                                    ? (
                                        (ratingCounts['ผ่าน'] / schoolScores.length) *
                                        100
                                      ).toFixed(2)
                                    : '0.00'}
                                  %
                                </td>
                              </tr>
                              <tr>
                                <td>ต้องปรับปรุง (65.00-74.99)</td>
                                <td>{ratingCounts['ต้องปรับปรุง'] || 0}</td>
                                <td>
                                  {schoolScores.length > 0
                                    ? (
                                        (ratingCounts['ต้องปรับปรุง'] / schoolScores.length) *
                                        100
                                      ).toFixed(2)
                                    : '0.00'}
                                  %
                                </td>
                              </tr>
                              <tr>
                                <td>ต้องปรับปรุงโดยด่วน (0.00-64.99)</td>
                                <td>{ratingCounts['ต้องปรับปรุงโดยด่วน'] || 0}</td>
                                <td>
                                  {schoolScores.length > 0
                                    ? (
                                        (ratingCounts['ต้องปรับปรุงโดยด่วน'] / schoolScores.length) *
                                        100
                                      ).toFixed(2)
                                    : '0.00'}
                                  %
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="card">
                      <div className="card-header">
                        <h3 className="card-title">กราฟแสดงผลการประเมิน</h3>
                      </div>
                      <div className="card-body">
                        {chartData ? (
                          <Bar data={chartData.data} options={chartData.options} />
                        ) : (
                          <div className="text-center py-5">
                            <p>ไม่มีข้อมูลสำหรับการแสดงกราฟ</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="card">
                  <div className="card-header">
                    <h3 className="card-title">ตารางคะแนนผลการประเมิน ITA</h3>
                  </div>
                  <div className="card-body table-responsive p-0">
                    <table className="table table-striped table-hover">
                      <thead>
                        <tr>
                          <th style={{ width: '5%' }}>ลำดับ</th>
                          <th style={{ width: '25%' }}>โรงเรียน</th>
                          <th style={{ width: '10%' }}>IIT (30)</th>
                          <th style={{ width: '10%' }}>EIT (30)</th>
                          <th style={{ width: '10%' }}>OIT (40)</th>
                          <th style={{ width: '10%' }}>คะแนนรวม</th>
                          <th style={{ width: '15%' }}>ระดับผล</th>
                          <th style={{ width: '15%' }}>ดูรายละเอียด</th>
                        </tr>
                      </thead>
                      <tbody>
                        {schoolScores.length > 0 ? (
                          schoolScores.map((school, index) => (
                            <tr key={school.school_id}>
                              <td>{index + 1}</td>
                              <td>{school.school_name}</td>
                              <td>{parseFloat(school.iit_score.toFixed(2))}</td>
                              <td>{parseFloat(school.eit_score.toFixed(2))}</td>
                              <td>{parseFloat(school.oit_score.toFixed(2))}</td>
                              <td className="font-weight-bold">
                                {parseFloat(school.total_score.toFixed(2))}
                              </td>
                              <td>
                                <span
                                  className={`badge ${
                                    school.rating === 'ผ่านดีเยี่ยม'
                                      ? 'bg-success'
                                      : school.rating === 'ผ่านดี'
                                      ? 'bg-info'
                                      : school.rating === 'ผ่าน'
                                      ? 'bg-primary'
                                      : school.rating === 'ต้องปรับปรุง'
                                      ? 'bg-warning'
                                      : 'bg-danger'
                                  }`}
                                >
                                  {school.rating}
                                </span>
                              </td>
                              <td>
                                <Link
                                  to={`/super-admin/reports/school/${school.school_id}?fiscalYear=${selectedFiscalYear}`}
                                  className="btn btn-primary btn-sm"
                                >
                                  <i className="fas fa-search"></i> รายละเอียด
                                </Link>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="8" className="text-center py-3">
                              ไม่พบข้อมูลผลการประเมิน
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}
          </div>
        </section>
      </div>

      <Footer />
    </div>
  );
};

export default ReportOverview;